/**
 * Privacy Disclosure modal — S5-T2.1.
 *
 * The dominant trust signal for the funnel (docs/web-strategy.md): a plain
 * "privacy report" that states the network-zero claim and, more importantly,
 * shows the visitor how to verify it themselves in the browser. The promise is
 * scoped precisely to the *document* — the page still loads its own code/fonts
 * — so the claim stays honest and the Network-tab demo holds up.
 */

import { useEffect, useRef } from "react";

import styles from "./PrivacyModal.module.css";

interface PrivacyModalProps {
  open: boolean;
  onClose: () => void;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export function PrivacyModal({ open, onClose }: PrivacyModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // The element focused before the dialog opened, so focus can be restored.
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    returnFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      // Focus trap: keep Tab cycling within the dialog.
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-modal-title"
      >
        <div className={styles.head}>
          <h2 id="privacy-modal-title" className={styles.title}>
            Privacy report
          </h2>
          <button
            ref={closeRef}
            type="button"
            className={styles.close}
            aria-label="Close privacy report"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <p className={styles.lead}>Your PDF never leaves your device.</p>

        <ul className={styles.points}>
          <li>Imprint stamps your PDF right here in your browser. There is no upload server.</li>
          <li>Your document is never sent to Starkly. No account, no sign-in, no copy on our end.</li>
          <li>We cannot leak a file we never receive.</li>
        </ul>

        <p className={styles.verify}>
          Check it yourself: open your browser&rsquo;s Network tab and re-stamp a PDF. This
          page&rsquo;s own code and fonts load, and nothing carries your document.
        </p>

        <p className={styles.policyLink}>
          The Mac app works the same way: documents are processed locally on your Mac, purchases
          go through Apple, and there are no analytics, ads, or account.{" "}
          <a
            className={styles.policyLinkAnchor}
            href="https://starkly.cc/apps/imprint/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Read the full privacy policy (opens in a new tab)"
          >
            Read the full privacy policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
